import { useEffect, useState, useCallback } from "react";
import { Loader2, Megaphone, MessageSquareText, GraduationCap, Paperclip, X, Landmark, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ChatThread, { readFileAsAttachment } from "@/components/ChatThread";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useUrlState } from "@/lib/useUrlState";

export default function MessagesPage() {
  const [classes, setClasses] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("school");
  const [classId, setClassId] = useState("");
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeStudentParam, setActiveStudentParam] = useUrlState("student", "");
  const activeStudent = activeStudentParam || null;
  const setActiveStudent = (id) => setActiveStudentParam(id || "");
  const [thread, setThread] = useState(null);
  const [officeThreads, setOfficeThreads] = useState([]);
  const [activeOfficeParam, setActiveOfficeParam] = useUrlState("office", "");
  const activeOffice = activeOfficeParam || null;
  const setActiveOffice = (id) => setActiveOfficeParam(id || "");
  const [officeThread, setOfficeThread] = useState(null);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sid = searchParams.get("student_id") || activeStudentParam;
    if (sid) openThread(sid);
    else if (activeOfficeParam) openOfficeThread(activeOfficeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mql = q.trim().toLowerCase();
  const visibleThreads = threads.filter((t) =>
    !mql ||
    (t.student_name || "").toLowerCase().includes(mql) ||
    (t.class_name || "").toLowerCase().includes(mql) ||
    (t.teacher_name || "").toLowerCase().includes(mql) ||
    (t.last_preview || "").toLowerCase().includes(mql)
  );
  const visibleOfficeThreads = officeThreads.filter((t) =>
    !mql ||
    (t.parent_name || "").toLowerCase().includes(mql) ||
    (t.parent_phone || "").toLowerCase().includes(mql) ||
    (t.children || []).some((c) => (c || "").toLowerCase().includes(mql)) ||
    (t.last_preview || "").toLowerCase().includes(mql)
  );

  const load = useCallback(async () => {
    try {
      const [c, t, o] = await Promise.all([
        api.get("/classes"),
        api.get("/messages/threads"),
        api.get("/office/threads"),
      ]);
      setClasses(c.data);
      setThreads(t.data);
      setOfficeThreads(o.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  const broadcast = async (e) => {
    e.preventDefault();
    if (!body.trim() && !attachment) return;
    if (scope === "class" && !classId) { toast.error("Pick a class first."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/messages/send", {
        scope, body: body.trim(),
        class_id: scope === "class" ? classId : undefined,
        attachment: attachment || undefined,
      });
      toast.success(`Sent to parents of ${data.sent_to} students`);
      setBody("");
      setAttachment(null);
      setComposeOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const openThread = async (studentId) => {
    setActiveStudent(studentId);
    setActiveOffice(null);
    setOfficeThread(null);
    setThread(null);
    try {
      const { data } = await api.get(`/messages/threads/${studentId}`);
      setThread(data);
      setThreads((ts) => ts.map((t) => (t.student_id === studentId ? { ...t, unread: 0 } : t)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const openOfficeThread = async (threadId) => {
    setActiveOffice(threadId);
    setActiveStudent(null);
    setThread(null);
    setOfficeThread(null);
    try {
      const { data } = await api.get(`/office/threads/${threadId}`);
      setOfficeThread(data);
      setOfficeThreads((ts) => ts.map((t) => (t.id === threadId ? { ...t, unread: 0 } : t)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const replyOffice = async (text, attachment) => {
    try {
      await api.post(`/office/threads/${activeOffice}`, { body: text, attachment: attachment || undefined });
      const { data } = await api.get(`/office/threads/${activeOffice}`);
      setOfficeThread(data);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      return false;
    }
  };

  const reply = async (text, attachment) => {
    try {
      await api.post("/messages/send", { scope: "student", student_id: activeStudent, body: text, attachment: attachment || undefined });
      const { data } = await api.get(`/messages/threads/${activeStudent}`);
      setThread(data);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      return false;
    }
  };

  return (
    <div className="p-6 md:p-10 h-full flex flex-col" data-testid="messages-page">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Parent communication</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Messages</h1>
          <p className="text-sm text-slate-600 mt-1">Announce to the whole school or one class — parent replies go to the class teacher, and you can see every thread here.</p>
        </div>
        <button
          onClick={() => setComposeOpen(true)}
          data-testid="school-announce-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 transition shrink-0"
        >
          <Megaphone className="h-4 w-4" /> Send announcement
        </button>
      </header>

      <Dialog open={composeOpen} onOpenChange={(o) => !o && setComposeOpen(false)}>
        <DialogContent className="sm:max-w-lg" data-testid="school-announce-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-600" /> Send announcement
            </DialogTitle>
            <DialogDescription>
              Delivered to every parent in the chosen scope; replies go to each child's grade teacher.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={broadcast} className="space-y-3 pt-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="broadcast-scope"
                className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary">
                <option value="school">Whole school</option>
                <option value="class">One grade</option>
              </select>
              {scope === "class" && (
                <select value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="broadcast-class"
                  className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary">
                  <option value="">Pick a grade…</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              autoFocus
              data-testid="broadcast-input"
              placeholder="e.g. Early dismissal Friday at 1:00 PM"
              className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
            />
            {attachment && (
              <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-medium" data-testid="broadcast-attachment-chip">
                <Paperclip className="h-3 w-3" /> {attachment.name}
                <button type="button" onClick={() => setAttachment(null)} data-testid="broadcast-attachment-remove"><X className="h-3 w-3" /></button>
              </span>
            )}
            <DialogFooter className="gap-2 sm:gap-2 sm:justify-between">
              <label className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 transition" title="Attach a photo or PDF">
                <Paperclip className="h-4 w-4" /> Attach file
                <input
                  type="file" accept="image/*,application/pdf" className="hidden" data-testid="broadcast-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) readFileAsAttachment(f, setAttachment, (err) => toast.error(err));
                  }}
                />
              </label>
              <button type="submit" disabled={sending || (!body.trim() && !attachment)} data-testid="broadcast-send"
                className="h-10 rounded-lg bg-amber-600 px-5 text-sm font-medium text-white inline-flex items-center justify-center gap-2 hover:bg-amber-700 disabled:opacity-50 transition">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} Send
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-0 rounded-xl border border-slate-200 bg-white overflow-hidden" style={{ minHeight: "420px" }}>
        <aside className="border-r border-slate-200 overflow-y-auto" data-testid="admin-threads">
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <MessageSquareText className="h-3.5 w-3.5" /> Conversations
          </div>
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="messages-search-input"
                placeholder="Search student, parent or message…"
                className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-xs focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </div>
          </div>
          {loading && <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>}
          {!loading && threads.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No conversations yet.</div>}
          {!loading && threads.length > 0 && visibleThreads.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No conversations match your search.</div>}
          {visibleThreads.map((t) => (
            <button key={t.student_id} onClick={() => openThread(t.student_id)} data-testid={`admin-thread-${t.student_id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${activeStudent === t.student_id ? "bg-primary/5" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900 text-sm truncate">{t.student_name}</span>
                {t.unread > 0 && <span className="shrink-0 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{t.unread}</span>}
              </div>
              <div className="text-[11px] text-slate-400 truncate">{[t.class_name, t.teacher_name].filter(Boolean).join(" · ")}</div>
              <div className="text-xs text-slate-400 truncate mt-0.5">{t.last_preview}</div>
            </button>
          ))}
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-2 bg-emerald-50/60" data-testid="office-inbox-label">
            <Landmark className="h-3.5 w-3.5 text-emerald-600" /> Office inbox
          </div>
          {!loading && officeThreads.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No parent questions yet — parents can reach the office from their app.</div>}
          {!loading && officeThreads.length > 0 && visibleOfficeThreads.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No office threads match your search.</div>}
          {visibleOfficeThreads.map((t) => (
            <button key={t.id} onClick={() => openOfficeThread(t.id)} data-testid={`office-thread-${t.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-emerald-50/50 transition ${activeOffice === t.id ? "bg-emerald-50" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-900 text-sm truncate">{t.parent_name || t.parent_phone}</span>
                {t.unread > 0 && <span className="shrink-0 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{t.unread}</span>}
              </div>
              <div className="text-[11px] text-slate-400 truncate">{t.children.length > 0 ? `Parent of ${t.children.join(", ")}` : t.parent_phone}</div>
              <div className="text-xs text-slate-400 truncate mt-0.5">{t.last_preview}</div>
            </button>
          ))}
        </aside>
        <section className="min-h-0 flex flex-col">
          {activeStudent && thread ? (
            <>
              <div className="border-b border-slate-200 px-4 py-2.5 flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><GraduationCap className="h-4 w-4" /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-900" data-testid="admin-chat-student">{thread.student.full_name}</div>
                  <div className="text-[11px] text-slate-400">{thread.student.contacts.map((c) => c.name).filter(Boolean).join(", ") || "No parent contacts"}</div>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <ChatThread messages={thread.messages} onSend={reply} mySenderType="staff" />
              </div>
            </>
          ) : activeOffice && officeThread ? (
            <>
              <div className="border-b border-slate-200 px-4 py-2.5 flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Landmark className="h-4 w-4" /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-900" data-testid="office-chat-parent">{officeThread.parent_name || officeThread.parent_phone}</div>
                  <div className="text-[11px] text-slate-400">Office question · {officeThread.parent_phone}</div>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <ChatThread messages={officeThread.messages} onSend={replyOffice} mySenderType="staff" />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-10">Pick a conversation to read and reply.</div>
          )}
        </section>
      </div>
    </div>
  );
}
